// src/viqai/pricing-viqai.js
// ─── Positionnement commercial des offres (§20, §21) ────────────────────────
// Règle posée par le cahier des charges et respectée strictement ici :
//
//     logique commerciale affichée  ≠  logique technique de facturation
//
// Ce fichier ne contient AUCUN montant, AUCUN code de plan Stripe, AUCUNE
// période. Il ne décrit que ce que le client achète. Les prix, les promotions
// et les abonnements continuent de venir de `server.js` (UNLIMITED_PLAN,
// MULTI_AGENT_PLAN, getPromoPricing) et de `services/stripe.js`, inchangés.
//
// Conséquence voulue : on peut refondre le discours commercial cette semaine
// et faire évoluer Stripe le mois prochain, sans qu'aucun abonnement en cours
// ne soit touché. Les codes techniques (`unlimited`, `multi_agent_3`) restent
// les mêmes — seul le vocabulaire visible change.
//
// §21 — Vocabulaire BANNI des cartes tarifaires principales, parce qu'il donne
// l'impression d'acheter une collection de boutons :
//     cote · argus · marge (comme module) · recherche achat · contrôle ·
//     recherche VO · malus · estimation · comparatif plateformes
// Ce sont désormais des capacités sous-jacentes de VIQ.AI, pas des produits.

// Correspondance offre affichée → plan technique existant. C'est le SEUL point
// de contact entre les deux logiques : un dictionnaire de 4 lignes qu'on peut
// modifier sans toucher au reste.
export const OFFER_TO_PLAN = {
  discovery: null,              // essai — aucun plan Stripe
  essential: "unlimited",       // ex-« Pro Solo »       (399 €/mois côté serveur)
  pro: "multi_agent_3",         // ex-« Pro Multi-Agent » (799 €/mois côté serveur)
  team: null,                   // ex-« Pro Multi-Agence » — sur devis
};

// Réciproque, pour afficher le bon libellé commercial à un abonné existant
// sans migrer sa ligne en base.
export const PLAN_TO_OFFER = {
  unlimited: "essential",
  multi_agent_3: "pro",
};

export const VIQAI_OFFERS = [
  {
    id: "discovery",
    label: "Découvrir VIQ.AI",
    eyebrow: "Essai",
    pitch: "Cinq analyses complètes pour voir ce que l'agent sait faire sur vos véhicules.",
    target: "Sans carte bancaire, sans engagement",
    capabilities: [
      "Agent IA automobile en conversation",
      "Analyse multimodale : photos, documents, plaque",
      "Intelligence véhicule complète",
      "Mémoire du véhicule sur 5 dossiers",
    ],
    cta: "Démarrer gratuitement",
    highlight: false,
  },
  {
    id: "essential",
    label: "VIQ.AI Essential",
    eyebrow: "⭐ Le plus choisi",
    pitch: "Un agent automobile qui travaille avec vous, tous les jours, sur tous vos véhicules.",
    target: "Professionnel indépendant · négociant · garage",
    capabilities: [
      "Assistant IA automobile illimité",
      "Analyse multimodale · photos, documents, factures",
      "Intelligence véhicule et fiabilité",
      "Dossiers intelligents à mémoire persistante",
      "Intelligence marché à la demande",
      "Analyse documentaire et administrative",
      "Moteurs Claude et ChatGPT selon la tâche",
      "Vision automobile sur vos photos",
      "Mémoire professionnelle de votre activité",
      "Formation offerte · résiliable à tout moment",
    ],
    cta: "Activer VIQ.AI Essential",
    highlight: true,
  },
  {
    id: "pro",
    label: "VIQ.AI Pro",
    eyebrow: "Intelligence avancée",
    pitch: "L'agent traite plusieurs véhicules de front et enchaîne les analyses sans que vous suiviez.",
    target: "Marchands à volume · équipes jusqu'à 3 collaborateurs",
    capabilities: [
      "Tout VIQ.AI Essential",
      "Intelligence automobile avancée",
      "Agents VehicleIQ en parallèle sur plusieurs véhicules",
      "Analyses IA étendues et enchaînées",
      "Automatisations IA sur vos dossiers",
      "Intelligence partagée entre collaborateurs",
      "3 accès inclus · rôles et permissions",
      "Compte dédié · SLA 99,5 % · support 24 h",
      "Mémoire d'entreprise",
      "Formation offerte · sans engagement",
    ],
    cta: "Passer à VIQ.AI Pro",
    highlight: false,
  },
  {
    id: "team",
    label: "VIQ.AI Team",
    eyebrow: "Réseaux & groupes",
    pitch: "L'intelligence de votre groupe, partagée entre tous vos sites.",
    target: "À partir de 4 accès ou plusieurs points de vente",
    capabilities: [
      "Tout VIQ.AI Pro",
      "Accès et sites illimités",
      "Intelligence partagée multi-établissements",
      "Mémoire d'entreprise consolidée",
      "Dossiers collaboratifs",
      "Administration d'équipe et permissions par site",
      "Historique de conversations partagé",
      "Capacités IA supérieures",
      "Intégration à votre système existant",
      "Interlocuteur dédié · accompagnement au déploiement",
    ],
    cta: "Construire votre offre",
    highlight: false,
  },
];

// Garde-fou de rédaction. Le vocabulaire banni revient toujours par la petite
// porte quand on ajoute une ligne « juste pour être précis » : ce contrôle le
// signale en développement, sans jamais casser l'affichage en production.
const BANNED = ["cote", "argus", "estimation", "recherche achat", "recherche vo", "malus", "contrôle vin", "comparatif plateforme"];

export function auditOfferWording(offers = VIQAI_OFFERS) {
  const hits = [];
  for (const o of offers) {
    for (const c of o.capabilities) {
      const low = c.toLowerCase();
      for (const b of BANNED) if (low.includes(b)) hits.push({ offer: o.id, line: c, term: b });
    }
  }
  return hits;
}

if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
  const hits = auditOfferWording();
  if (hits.length) console.warn("[pricing-viqai] vocabulaire d'ancienne génération dans les offres :", hits);
}
